import TextAreaAutoSize from "react-textarea-autosize";
import styled, { css } from "styled-components";
import { CoreTypography, CoreTextArea } from "@boi/components-core";
import { parseSizeToken, useDesignTokens } from "@boi/react-design-tokens";

type WrapperProps = {
  $displayInnerShadow: CoreTextArea.DisplayInnerShadow;
};

export const Wrapper = styled.div<WrapperProps>`
  display: flex;
  position: relative;

  ${({ $displayInnerShadow }) => {
    const { tokens } = useDesignTokens();
    const { textArea, outerField } = tokens;

    return css`
      &::before,
      &::after {
        content: "";
        position: absolute;
        width: 100%;
        height: ${textArea.innerShadow.height};
        pointer-events: none;
        opacity: ${textArea.innerShadow.opacity.inactive};
        transition: opacity 0.3s;
      }
      &::before {
        top: 0px;
        box-shadow: 0 4px 4px ${textArea.innerShadow.color} inset;
        border-radius: ${outerField.radius} ${outerField.radius} 0 0;
      }
      &::after {
        bottom: 0px;
        box-shadow: 0 -4px 4px ${textArea.innerShadow.color} inset;
        border-radius: 0 0 ${outerField.radius} ${outerField.radius};
      }

      ${() =>
        ($displayInnerShadow === "TOP" || $displayInnerShadow === "BOTH") &&
        css`
          &::before {
            opacity: ${textArea.innerShadow.opacity.active};
          }
        `}

      ${() =>
        ($displayInnerShadow === "BOTTOM" || $displayInnerShadow === "BOTH") &&
        css`
        &::after {
          opacity: ${textArea.innerShadow.opacity.active};
      `}
    `;
  }}
`;

export const CustomTextArea = styled(TextAreaAutoSize)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { textArea, typography, scrollBar, outerField } = tokens;

    const typographyVariant: CoreTypography.Variant =
      textArea.typography.variant;

    const fontSize = parseSizeToken(typography.fontSize[typographyVariant]);
    const lineHeight = parseSizeToken(typography.lineHeight[typographyVariant]);

    return css`
      all: unset;
      white-space: pre-wrap;
      word-wrap: break-word;

      width: 100%;
      resize: none;
      min-height: ${textArea.minHeight};
      height: 100%;
      padding: calc(${textArea.paddingVertical} - 2px)
        calc(${textArea.paddingHorizontal} - 2px);
      margin: 2px;

      color: ${textArea.color.base};
      font-family: ${typography.fontFamily[typographyVariant]};
      font-size: ${fontSize};
      line-height: ${lineHeight};

      &:read-only {
        color: ${textArea.color.readOnly};
      }
      &:disabled {
        color: ${textArea.color.disabled};
      }

      scrollbar-color: ${scrollBar.thumbColor} transparent;

      /* Workaround for Safari */
      @supports not (scrollbar-color: ${scrollBar.thumbColor} transparent) {
        &::-webkit-scrollbar,
        &::-webkit-scrollbar-corner {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: ${scrollBar.thumbColor};
          border-radius: ${scrollBar.radius};
          border: 3.5px solid ${outerField.backgroundColor};
        }
      }
    `;
  }}
`;
